/**
 * Selection Configuration — Adaptive Engine v11.2
 *
 * Centralized configuration constants and debug utilities
 * for the question selection algorithm.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import { debug as logDebug } from '../../utils/logger'

// Debug flag — enabled in development, disabled in production
export const ADAPTIVE_DEBUG = process.env.NODE_ENV === 'development'

export function debugLog(...args: unknown[]) {
  if (ADAPTIVE_DEBUG) logDebug(...args)
}

// v11.2 Configuration - Hardened Pedagogical Rules
// NOTE: STRUGGLING/LEARNING/MASTERED thresholds (40/70/85) are mirrored in
// config/constants.ts MASTERY_LEVEL_THRESHOLDS for UI display purposes.
// If pedagogical boundaries change, update BOTH locations. (D-SCAN-2 D2-5)
export const V11_CONFIG = {
  /** Mastery score below which a family is considered "struggling" */
  STRUGGLING_THRESHOLD: 40,
  /** Mastery score below which a family is considered "learning" */
  LEARNING_THRESHOLD: 70,
  /** Mastery score above which a family is considered "mastered" */
  MASTERED_THRESHOLD: 85,
  /** Minimum attempts before mastery is considered stable */
  MASTERY_CONFIDENCE_MIN_ATTEMPTS: 5,
  /** Maximum number of mastered-family questions per session (soft cap) */
  MAX_MASTERED_PER_SESSION: 2,
  /** Maximum percentage of session that can be mastered content (70%) */
  MAX_MASTERED_PERCENTAGE: 0.7,
  /** Maximum questions from same family per session */
  MAX_QUESTIONS_PER_FAMILY: 2,
  /** Minimum different families per session (if available) */
  MIN_FAMILIES_PER_SESSION: 2,
  /** Recency penalty: reduce probability by 50% if used in last session */
  RECENCY_PENALTY_MULTIPLIER: 0.5,
  /** Tier window: how many tiers below target to include */
  TIER_WINDOW_BELOW: 1,
  /** Tier window: how many tiers above target to include */
  TIER_WINDOW_ABOVE: 1,
  /** Expanded tier window for fallback attempts */
  TIER_WINDOW_FALLBACK: 3,
  /** Number of top-priority families to randomly pick from */
  TOP_CANDIDATE_POOL: 4,
  /** Cooldown: recently-seen familyIds to deprioritize */
  RECENT_FAMILY_COOLDOWN: 5,
  /** Max retries per slot */
  MAX_RETRIES_PER_SLOT: 5,
  /** Max total retries across session */
  MAX_TOTAL_RETRIES: 50,
  /** Min tier value */
  MIN_TIER: 1,
  /** Max tier value (16-tier system) */
  MAX_TIER: 16,
  /** Level unlock threshold (ratio of mastered/practiced questions) */
  LEVEL_UNLOCK_THRESHOLD: 0.5,
  /** Minimum questions attempted at a level before unlock is possible */
  LEVEL_UNLOCK_MIN_ATTEMPTED: 1,
  /** Soft preference: prefer interactive questions after this many consecutive MC questions */
  MAX_CONSECUTIVE_MC: 3,
  /**
   * LDG prerequisite mastery threshold.
   * A student must reach this mastery on ALL hard prerequisites before
   * the dependent family enters the selection pool.
   *
   * Pedagogical rationale: At 60 mastery, the student has demonstrated
   * consistent understanding of the prerequisite concept — not just
   * lucky guesses (40 would be too low) but not requiring full mastery
   * (85 would be too restrictive and slow progression).
   *
   * This is intentionally HIGHER than STRUGGLING_THRESHOLD (40) because
   * prerequisite gating should be stricter than general priority scoring.
   * A student at 40% on fractions basics should NOT yet receive fraction
   * addition questions.
   *
   * v11.4: Raised from 40 (STRUGGLING_THRESHOLD) to 60 for tighter LDG enforcement.
   */
  LDG_PREREQUISITE_MASTERY: 60,
  /**
   * Plus-level unlock mastery threshold.
   * Students must reach this mastery on the base tier before plus-tier
   * questions are included in the selection window.
   *
   * Pedagogical rationale: Plus questions require transfer/analysis thinking.
   * Students who haven't consolidated the base procedure will get stuck
   * on plus questions, creating frustration instead of challenge.
   *
   * At 55 mastery, the student has demonstrated consistent understanding
   * of the base concept (not just lucky guesses) but hasn't fully mastered it,
   * making plus questions an appropriate stretch challenge.
   */
  PLUS_UNLOCK_MASTERY: 55,
  /**
   * B/M/E proximity boost for new (untested) families.
   * When a student's B/M/E period tier is set, families whose tier
   * is closer to that period tier get a priority bonus.
   *
   * Pedagogical rationale (QF-26 Optie B): Teachers/parents set B/M/E
   * expecting it to influence difficulty. Without this boost, B/M/E has
   * no effect once the student has any mastery data. The soft boost
   * ensures B7 students see B7-tier families first, while still allowing
   * progression to M7/E7 families as mastery grows.
   *
   * Only applies to NEW families (no mastery data). Once a family has
   * mastery, the mastery score drives priority — B/M/E should not
   * override actual performance data.
   */
  BME_PROXIMITY_BOOST_EXACT: 20,
  BME_PROXIMITY_BOOST_NEAR: 10, // within 1 tier
  BME_PROXIMITY_BOOST_FAR: 5, // within 2 tiers

  /**
   * Minimum DOK (Depth of Knowledge) per tier band.
   * Questions with a DOK below this threshold are filtered out
   * of the selection pool for that tier band.
   *
   * PEDAGOGICAL_CONTRACT_SPEC §6: Ensures cognitive depth matches
   * difficulty level. Only applies to questions that HAVE a DOK tag
   * (backwards compatible — untagged questions pass through).
   */
  // RC pass: keyed by a literal union so typo'd lookups
  // (`MIN_DOK_FOR_TIER_BAND['basicc']`) fail at compile time.
  MIN_DOK_FOR_TIER_BAND: {
    basic: 1, // tier 1-4: recall is fine
    intermediate: 1, // tier 5-8: recall still acceptable
    advanced: 2, // tier 9-12: must be skill/concept or higher
    expert: 3, // tier 13-16: must be strategic thinking or higher
  } as Record<'basic' | 'intermediate' | 'advanced' | 'expert', number>,

  /**
   * Skill interleaving — P-11 (Audit Verbeterplan v4.1)
   *
   * Prevents same-skill clustering by penalizing families whose skill
   * matches the most recent questions. Based on interleaving research
   * (Rohrer & Taylor, 2007): mixing skills during practice improves
   * long-term retention vs. blocked practice.
   *
   * SKILL_INTERLEAVE_WINDOW: How many recent skills to check.
   * SKILL_INTERLEAVE_PENALTY: Multiplier applied to priority score
   *   when the family's skill matches a recent skill (0.6 = 40% reduction).
   */
  SKILL_INTERLEAVE_WINDOW: 2,
  SKILL_INTERLEAVE_PENALTY: 0.6,

  /**
   * Cognitive level diversity — P-10 (Audit Verbeterplan v4.1)
   *
   * Soft preference for cognitive level variety in question selection.
   * When the last N questions were all the same cognitive level (e.g.,
   * all 'recall'), prefer candidates at a different level.
   *
   * This is a SOFT preference: if all candidates are the same level,
   * they still pass through. Only filters when alternatives exist.
   *
   * COGNITIVE_DIVERSITY_WINDOW: How many recent cognitive levels to check.
   */
  COGNITIVE_DIVERSITY_WINDOW: 3,

  /**
   * Frustration detection — P-12 (Audit Verbeterplan v4.1)
   *
   * When a learner gets FRUSTRATION_CONSECUTIVE_ERRORS consecutive wrong
   * answers on the SAME family, remaining questions from that family are
   * swapped out for questions from different families.
   *
   * Pedagogical rationale: Repeated failure on the same concept creates
   * a frustration spiral. Switching to a different concept gives the
   * learner a fresh start and prevents learned helplessness. The
   * frustrated family will reappear in future sessions once the learner
   * has had time to consolidate.
   *
   * This is transparent to the learner — the session continues normally
   * with different questions. A cheat sheet nudge may also fire (existing
   * behavior at 3 global consecutive errors).
   */
  FRUSTRATION_CONSECUTIVE_ERRORS: 3,

  /**
   * Mid-session difficulty adjustment — P-13 (Audit Verbeterplan v4.x)
   *
   * Dynamically adjusts the difficulty of upcoming questions based on
   * the learner's running accuracy within the current session. This
   * creates a "flow channel" — keeping the learner challenged but not
   * frustrated, complementary to P-12 (which swaps families on frustration).
   *
   * DIFFICULTY_ADJUST_WINDOW: Number of recent answers to evaluate.
   * DIFFICULTY_ADJUST_UP_THRESHOLD: Accuracy above this → swap upcoming
   *   questions for harder ones (higher tier).
   * DIFFICULTY_ADJUST_DOWN_THRESHOLD: Accuracy below this → swap upcoming
   *   questions for easier ones (lower tier).
   * DIFFICULTY_ADJUST_TIER_SHIFT: How many tiers to shift when adjusting.
   * DIFFICULTY_ADJUST_COOLDOWN: Minimum answers between adjustments to
   *   prevent oscillation.
   *
   * Set thresholds to 0 to disable adjustment in that direction.
   */
  DIFFICULTY_ADJUST_WINDOW: 5,
  DIFFICULTY_ADJUST_UP_THRESHOLD: 0.85,
  DIFFICULTY_ADJUST_DOWN_THRESHOLD: 0.35,
  DIFFICULTY_ADJUST_TIER_SHIFT: 1,
  DIFFICULTY_ADJUST_COOLDOWN: 4,
}
